import React, { useState } from 'react';
import { useSignIn } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';

type Step = 'start' | 'verify-email' | 'verify-totp' | 'forgot-password' | 'reset-password';
type VerifyStrategy = 'email_code' | 'email_code_second' | 'totp';

const inputClass =
  'w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50';
const labelClass = 'block text-xs font-medium text-muted-foreground mb-1.5';
const errorClass = 'mt-1 text-xs text-destructive';
const submitClass =
  'w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50';
const cardClass =
  'w-full max-w-[400px] rounded-xl border border-border bg-card p-8 shadow-lg';

const GoogleIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const CustomSignIn: React.FC = () => {
  const { signIn, setActive, isLoaded } = useSignIn();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('start');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Which Clerk call the verify form submits to. `email_code` is a FIRST
  // factor (prepareFirstFactor / attemptFirstFactor); `email_code_second`
  // is the same strategy name used as a SECOND factor, which is what Clerk
  // sends for device verification on an unrecognised browser when TOTP is
  // not enabled on the instance (2026-10-15 live: `needs_second_factor`
  // with `supportedSecondFactors: [{ strategy: 'email_code' }]`). Treating
  // that as TOTP asked for an authenticator code nobody had, and never
  // called prepareSecondFactor, so no email was ever sent.
  const [verifyStrategy, setVerifyStrategy] = useState<VerifyStrategy>('email_code');

  if (!isLoaded || !signIn) return null;

  const clerkError = (err: unknown) =>
    ((err as any)?.errors?.[0]?.longMessage ??
      (err as any)?.errors?.[0]?.message ??
      'Something went wrong');

  const handleGoogle = async () => {
    try {
      await signIn.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: `${window.location.origin}/sso-callback`,
        redirectUrlComplete: '/dashboard/library',
      });
    } catch (err) {
      setError(clerkError(err));
    }
  };

  const handleStart = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await signIn.create({ identifier: email, password });
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        navigate('/dashboard/library');
      } else if (result.status === 'needs_first_factor') {
        const factors = result.supportedFirstFactors ?? [];
        const emailFactor = factors.find((f) => f.strategy === 'email_code');
        if (emailFactor && 'emailAddressId' in emailFactor) {
          await signIn.prepareFirstFactor({
            strategy: 'email_code',
            emailAddressId: (emailFactor as any).emailAddressId,
          });
          setVerifyStrategy('email_code');
        } else {
          setVerifyStrategy('totp');
        }
        setStep('verify-email');
      } else if (result.status === 'needs_second_factor') {
        const factors = result.supportedSecondFactors ?? [];
        const emailFactor = factors.find((f) => f.strategy === 'email_code');
        if (emailFactor) {
          // Clerk only sends the email once prepareSecondFactor is called.
          // @clerk/clerk-react 5.x: EmailCodeSecondFactorConfig.emailAddressId
          // is optional, but pass it through when the factor carries one.
          const emailAddressId = (emailFactor as { emailAddressId?: string }).emailAddressId;
          await signIn.prepareSecondFactor({
            strategy: 'email_code',
            ...(emailAddressId ? { emailAddressId } : {}),
          });
          setVerifyStrategy('email_code_second');
          setStep('verify-email');
        } else {
          setVerifyStrategy('totp');
          setStep('verify-totp');
        }
      }
    } catch (err) {
      setError(clerkError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result =
        verifyStrategy === 'totp'
          ? await signIn.attemptSecondFactor({ strategy: 'totp', code })
          : verifyStrategy === 'email_code_second'
            ? await signIn.attemptSecondFactor({ strategy: 'email_code', code })
            : await signIn.attemptFirstFactor({ strategy: 'email_code', code });
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        navigate('/dashboard/library');
      }
    } catch (err) {
      setError(clerkError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn.create({ strategy: 'reset_password_email_code', identifier: email });
      setStep('reset-password');
    } catch (err) {
      setError(clerkError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const result = await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code,
        password: newPassword,
      } as any);
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        navigate('/dashboard/library');
      }
    } catch (err) {
      setError(clerkError(err));
    } finally {
      setLoading(false);
    }
  };

  if (step === 'forgot-password') {
    return (
      <form onSubmit={handleForgotPassword} className={cardClass}>
        <h2 className="mb-1 text-xl font-semibold text-foreground">Reset password</h2>
        <p className="mb-6 text-sm text-muted-foreground">Enter your email to receive a reset code.</p>
        <div className="mb-6">
          <label className={labelClass}>Email address</label>
          <input
            type="email" autoComplete="email" required
            value={email} onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
          />
        </div>
        {error && <p className={errorClass}>{error}</p>}
        <button type="submit" disabled={loading} className={submitClass}>
          {loading ? 'Sending…' : 'Send reset code'}
        </button>
        <button
          type="button"
          onClick={() => { setStep('start'); setError(''); }}
          className="mt-3 w-full rounded-md border border-border px-4 py-2.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          Back to sign in
        </button>
      </form>
    );
  }

  if (step === 'reset-password') {
    return (
      <form onSubmit={handleResetPassword} className={cardClass}>
        <h2 className="mb-1 text-xl font-semibold text-foreground">Choose a new password</h2>
        <p className="mb-6 text-sm text-muted-foreground">Your new password must be at least 8 characters.</p>
        <div className="mb-4">
          <label className={labelClass}>Reset code</label>
          <input
            type="text" inputMode="numeric" autoComplete="one-time-code" required
            value={code} onChange={(e) => setCode(e.target.value)}
            className={inputClass}
          />
        </div>
        <div className="mb-4">
          <label className={labelClass}>New password</label>
          <input
            type="password" autoComplete="new-password" required
            value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
            className={inputClass}
          />
        </div>
        <div className="mb-6">
          <label className={labelClass}>Confirm password</label>
          <input
            type="password" autoComplete="new-password" required
            value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
            className={inputClass}
          />
        </div>
        {error && <p className={errorClass}>{error}</p>}
        <button type="submit" disabled={loading} className={submitClass}>
          {loading ? 'Resetting…' : 'Reset password'}
        </button>
      </form>
    );
  }

  if (step === 'verify-email' || step === 'verify-totp') {
    const isTotp = step === 'verify-totp';
    return (
      <form onSubmit={handleVerify} className={cardClass}>
        <h2 className="mb-1 text-xl font-semibold text-foreground">Verify your identity</h2>
        <p className="mb-6 text-sm text-muted-foreground">
          {isTotp ? 'Enter the code from your authenticator app.' : 'Enter the code we sent to your email.'}
        </p>
        <div className="mb-6">
          <label className={labelClass}>{isTotp ? 'Authenticator code' : 'Verification code'}</label>
          <input
            type="text" inputMode="numeric" autoComplete="one-time-code" required
            value={code} onChange={(e) => setCode(e.target.value)}
            className={inputClass}
          />
        </div>
        {error && <p className={errorClass}>{error}</p>}
        <button type="submit" disabled={loading} className={submitClass}>
          {loading ? 'Verifying…' : 'Continue'}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleStart} className={cardClass}>
      <h2 className="mb-1 text-xl font-semibold text-foreground">Sign in</h2>
      <p className="mb-6 text-sm text-muted-foreground">Enter your details to continue.</p>

      <button
        type="button"
        onClick={handleGoogle}
        className="mb-4 flex w-full items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
      >
        <GoogleIcon />
        Continue with Google
      </button>

      <div className="mb-5 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" />
        <span>or</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <div className="mb-4">
        <label className={labelClass}>Email address</label>
        <input
          type="email" autoComplete="email" required
          value={email} onChange={(e) => setEmail(e.target.value)}
          className={inputClass}
        />
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs font-medium text-muted-foreground">Password</label>
          <button
            type="button"
            onClick={() => { setStep('forgot-password'); setError(''); }}
            className="text-xs text-primary hover:underline"
          >
            Forgot password?
          </button>
        </div>
        <input
          type="password" autoComplete="current-password" required
          value={password} onChange={(e) => setPassword(e.target.value)}
          className={inputClass}
        />
      </div>

      {error && <p className={errorClass}>{error}</p>}

      <button type="submit" disabled={loading} className={submitClass}>
        {loading ? 'Signing in…' : 'Sign in'}
      </button>

      <p className="mt-5 text-center text-xs text-muted-foreground">
        Don&apos;t have an account?{' '}
        <a href="/sign-up" className="text-primary hover:underline">Create one</a>
      </p>
    </form>
  );
};

export default CustomSignIn;
